import { useCallback, useRef, useState } from 'react';
import isFileTypeAccepted from '@/utils/file/isFileTypeAccepted';

interface UseDragAndDropOptions {
  accept: string;
  validateAndProcessFile: (file: File) => boolean;
}

/**
 * Hook to manage drag and drop interactions with file type validation
 */
const useDragAndDrop = ({
  accept,
  validateAndProcessFile,
}: UseDragAndDropOptions) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragInvalid, setIsDragInvalid] = useState(false);
  const dragCounter = useRef(0);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setIsDragInvalid(false);
      dragCounter.current = 0;
      const file = e.dataTransfer.files?.[0];
      if (file) {
        validateAndProcessFile(file);
      }
    },
    [validateAndProcessFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        // Check if the dragged file type is valid
        const item = e.dataTransfer.items[0];
        const fileType = item.type;
        const isValid = fileType
          ? isFileTypeAccepted({ fileType, accept })
          : true; // Allow if type unknown
        setIsDragging(true);
        setIsDragInvalid(!isValid);
      }
    },
    [accept]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsDragInvalid(false);
    }
  }, []);

  return {
    isDragging,
    isDragInvalid,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
  };
};

export default useDragAndDrop;
